import * as THREE from 'three';
import { SceneManager } from './managers/SceneManager.js';
import { AssetManager } from './managers/AssetManager.js';
import { RenderLoop } from './core/RenderLoop.js';
import { MovementController } from './controllers/MovementController.js';
import { RemotePlayersManager } from './managers/RemotePlayersManager.js';
import { InventoryManager } from './managers/InventoryManager.js';
import { CombatManager } from './managers/CombatManager.js';
import { EnemyManager } from './managers/EnemyManager.js';
import { EnemyRenderer } from './managers/EnemyRenderer.js';
import { io } from 'socket.io-client';
import './test/attackAnimationTest.js';
import { ProjectileManager } from './managers/ProjectileManager.js';
import { UIManager } from './managers/UIManager.js';
import { StaticCharacterManager } from './managers/StaticCharacterManager.js';
import { VibeversePortalManager } from './managers/VibeversePortalManager.js';

/**
 * Main Game class
 */
export class Game {
    constructor() {
        // Core components
        this.sceneManager = null;
        this.assetManager = null;
        this.renderer = null;
        this.scene = null;
        this.camera = null;
        this.renderLoop = null;
        this.clock = new THREE.Clock();
        
        // Networking
        this.socket = null;
        this.connected = false;
        this.directConnectionAttempted = false;
        
        // Game state
        this.isRunning = false;
        this.player = null;
        this.playerData = null;
        this.remotePlayersManager = null;
        this.lastTimestamp = 0;
        this.deltaTime = 0;
        
        // Systems
        this.movementController = null;
        this.inventoryManager = null;
        this.combatManager = null;
        this.enemyManager = null;
        this.enemyRenderer = null;
        
        // Game entities
        this.entities = [];
        
        // Event queues for systems that might not be ready when events arrive
        this.pendingEvents = {
            enemySpawn: []  // Queue for enemy:spawn events
        };
        
        // Bind methods
        this.update = this.update.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        
        // Canvas element
        this.canvas = null;
        
        // ProjectileManager
        this.projectileManager = null;
        
        // Ensure we have a UI manager reference - USE GLOBAL REFERENCE FIRST
        if (window.uiManager) {
            console.log('Using existing global UIManager instance');
            this.uiManager = window.uiManager;
        } else {
            console.warn('UI Manager not available: No global UIManager instance found');
        }
        
        // Game initialization variables
        this.gameStarted = false;
        this.lastUpdate = Date.now();
        this.FPS = 60;
        this.players = new Map();
        this.enemies = [];
        
        // Initialize Three.js components
        this._initializeThree();
        
        // Initialize UI components
        this._initializeUI();
        
        // Make the game object globally accessible for debugging
        window.game = this;
        
        // Start the game loop
        this._gameLoop();
        
        console.log('Game instance created');
        
        // // Add StaticCharacterManager
        // this.staticCharacterManager = null;
        
        // Add VibeversePortalManager
        this.vibeversePortalManager = null;
    }
    
    /**
     * Initialize Three.js components
     * @private
     */
    _initializeThree() {
        console.log('Initializing Three.js components...');
        
        // Get canvas
        this.canvas = document.getElementById('game-canvas');
        if (!this.canvas) {
            console.error('Cannot find game canvas');
            return false;
        }
        
        // Create renderer
        this.renderer = new THREE.WebGLRenderer({ 
            canvas: this.canvas,
            antialias: true
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        
        // Create scene manager
        this.sceneManager = new SceneManager();
        
        // Initialize scene manager with renderer
        if (this.sceneManager && typeof this.sceneManager.init === 'function') {
            if (!this.sceneManager.init(this.renderer)) {
                console.error('Failed to initialize SceneManager');
                return false;
            }
            
            // Store references to scene and camera from SceneManager
            this.scene = this.sceneManager.scene;
            this.camera = this.sceneManager.camera;
        } else {
            // Create basic scene and camera if SceneManager isn't available
            this.scene = new THREE.Scene();
            this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
            this.camera.position.set(0, 5, 10);
            this.camera.lookAt(0, 0, 0);
            console.warn('Using fallback scene and camera setup');
        }
        
        // Initialize window resize handler
        window.addEventListener('resize', this.onWindowResize.bind(this));
        
        // Initialize frame tracking for performance monitoring
        this.lastFrameTime = performance.now();
        this.frameCount = 0;
        this.totalFrameTime = 0;
        this.fps = 0;
        
        console.log('Three.js components initialized successfully');
        return true;
    }
    
    /**
     * Main game loop - called each frame
     * @private
     */
    _gameLoop() {
        // Check if the game is fully initialized
        if (!this.initialized) {
            requestAnimationFrame(this._gameLoop.bind(this));
            return;
        }
        
        // Calculate delta time
        const currentTime = performance.now();
        const deltaTime = (currentTime - this.lastFrameTime) / 1000;
        this.lastFrameTime = currentTime;
        
        // Record performance for debug
        this.frameCount++;
        this.totalFrameTime += deltaTime;
        if (this.totalFrameTime >= 1.0) {
            this.fps = this.frameCount / this.totalFrameTime;
            this.frameCount = 0;
            this.totalFrameTime = 0;
        }
        
        // Process any network updates that arrived during the last frame
        this._processPendingEvents();
        
        // Update the game scene
        this.sceneManager.update(deltaTime);
        
        // Update player if it exists
        if (this.player && this.player.update) {
            // Check if player is dead
            const isPlayerDead = this.player.health <= 0;
            
            // Only update player if alive
            if (!isPlayerDead) {
                this.player.update(deltaTime);
            } else if (this.movementController && this.movementController.enabled) {
                // Disable movement controller if player is dead and it's still enabled
                console.log('Player is dead, disabling movement controller');
                this.movementController.enabled = false;
            }
        }
        
        // Update enemies if they exist
        if (this.enemyManager) {
            this.enemyManager.update(deltaTime);
        }
        
        // Render the scene - use the renderer directly instead of sceneManager.render()
        if (this.renderer && this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera);
        }
        
        // // Update static character manager
        // if (this.staticCharacterManager && this.player) {
        //     this.staticCharacterManager.update(this.player.position);
        // }
        
        // Update vibeverse portal manager
        if (this.vibeversePortalManager && this.player) {
            this.vibeversePortalManager.update(this.player);
        }
        
        // Schedule the next frame
        requestAnimationFrame(this._gameLoop.bind(this));
    }
    
    /**
     * Initialize the game
     * @returns {boolean} Success
     */
    init() {
        console.log('Initializing game...');
        
        try {
            // Initialize Three.js components
            if (!this._initializeThree()) {
                console.error('Failed to initialize Three.js components');
                return false;
            }
            
            // Initialize asset manager
            this.assetManager = new AssetManager();
            
            // Initialize render loop
            this.renderLoop = new RenderLoop(this.renderer, this.scene, this.camera, this.update);
            
            // Initialize network connection
            this._connectToServer();
            
            console.log('Game initialized');
            
            // Initialize client state (loading -> login -> characterSelect -> game)
            this.initialized = true;
            
            return true;
        } catch (error) {
            console.error('Error initializing game:', error);
            return false;
        }
    }

    /**
     * Connect to the game server
     * @private
     */
    _connectToServer() {
        console.log('Attempting to connect to game server...');
        
        // If we already have a socket with a connection, don't create a new one
        if (this.socket && this.connected) {
            console.log('Already connected to server.');
            return;
        }
        
        try {
            // Use relative URL instead of hardcoded port
            // This allows proper WebSocket protocol selection (ws vs wss)
            const serverUrl = '/';
            console.log(`Connecting to server at: ${serverUrl} (relative URL)`);
            
            // Connect to server via Socket.IO
            this.socket = io(serverUrl, {
                reconnection: true,
                reconnectionAttempts: 5,
                reconnectionDelay: 1000,
                transports: ['websocket']
            });
            
            // Connection established
            this.socket.on('connect', () => {
                console.log('Connected to server. Socket ID:', this.socket.id);
                this.connected = true;
                
                // Set status indicator
                const statusIndicator = document.getElementById('connection-status');
                if (statusIndicator) {
                    statusIndicator.classList.remove('disconnected');
                    statusIndicator.classList.add('connected');
                    statusIndicator.textContent = 'Connected';
                }
                
                // At this point, we're not creating RemotePlayersManager immediately
                // Instead, it will be created after assets are loaded
                
                // Send login information (guest mode)
                this._sendGuestLogin();
            });
            
            // Connection error
            this.socket.on('connect_error', (error) => {
                console.error('Connection error:', error);
                
                const statusIndicator = document.getElementById('connection-status');
                if (statusIndicator) {
                    statusIndicator.classList.remove('connected');
                    statusIndicator.classList.add('disconnected');
                    statusIndicator.textContent = 'Disconnected';
                }
                
                // Try direct connection if we haven't already
                if (!this.directConnectionAttempted) {
                    this._tryDirectConnection();
                }
            });
            
            // Disconnection
            this.socket.on('disconnect', () => {
                console.log('Disconnected from server');
                this.connected = false;
                
                const statusIndicator = document.getElementById('connection-status');
                if (statusIndicator) {
                    statusIndicator.classList.remove('connected');
                    statusIndicator.classList.add('disconnected');
                    statusIndicator.textContent = 'Disconnected';
                }
            });
            
            // Server error
            this.socket.on('error', (error) => {
                console.error('Server error:', error);
            });
            
            // Handle player updates (including health) from the server
            this.socket.on('playerUpdate', (data) => {
                console.log(`%%%%%%%%%%%%%%%%%%%%%%%%%%%%%`);
                console.log(`Received playerUpdate event for player ${data.id}`);
                
                // Check if this is the local player
                if (this.player && this.socket && this.socket.id === data.id) {
                    console.log(`Local player ${data.id} received update from ${data.damageSource || 'server'}`);
                    
                    // Track health updates for reliable damage application
                    const processedHealthUpdates = this.processedHealthUpdates || new Set();
                    this.processedHealthUpdates = processedHealthUpdates;
                    
                    // Check if this specific health update has already been processed
                    // Use attackId if available, otherwise use timestamp
                    const updateId = data.attackId || data.timestamp || Date.now();
                    
                    if (processedHealthUpdates.has(updateId)) {
                        console.log(`Skipping duplicate health update ${updateId}`);
                        return;
                    } else {
                        // Mark this update as processed
                        processedHealthUpdates.add(updateId);
                        
                        // Limit the size of the processed updates set
                        if (processedHealthUpdates.size > 100) {
                            // Remove oldest entries (convert to array, slice, and convert back)
                            const entries = Array.from(processedHealthUpdates);
                            const newEntries = entries.slice(entries.length - 50);
                            this.processedHealthUpdates = new Set(newEntries);
                        }
                    }
                    
                    // Check if isDead flag is set by the server
                    if (data.isDead === true) {
                        console.log(`Server indicates player ${data.id} is dead`);
                        // Call player death handler if the player isn't already marked as dead
                        if (!this.player.isDead) {
                            this.onPlayerDeath(this.player);
                        }
                        return; // Don't process further updates for dead players
                    }
                    
                    // Check if health data is included
                    if (data.health !== undefined) {
                        // Calculate how much health decreased
                        const oldHealth = this.player.health || 100;
                        const healthDecreased = oldHealth > data.health;
                        const healthDiff = healthDecreased ? oldHealth - data.health : 0;
                        
                        console.log(`Health change: ${oldHealth} -> ${data.health} (diff: ${healthDiff})`);
                        
                        // Update the player's health - IMPORTANT: do this after checking for damage
                        this.player.health = data.health;
                        
                        // Check if health reached 0, trigger death if so
                        if (data.health <= 0 && !this.player.isDead) {
                            console.log("Player health reached 0, triggering death sequence");
                            this.onPlayerDeath(this.player);
                            return; // Stop processing after death
                        }
                        
                        // Update the UI health bar if UI manager is available
                        // First try this.uiManager (our instance reference)
                        if (this.uiManager) {
                            console.log(`Updating UI health bar using this.uiManager`);
                            this.uiManager.updateHealth(data.health, this.player.maxHealth, healthDecreased);
                        } 
                        // Then try window.uiManager (global reference)
                        else if (window.uiManager) {
                            console.log(`Updating UI health bar using window.uiManager`);
                            window.uiManager.updateHealth(data.health, this.player.maxHealth, healthDecreased);
                        } 
                        // Direct DOM manipulation as last resort if UI managers aren't available
                        else {
                            console.warn(`No UI manager available, attempting direct DOM update`);
                            this._updateHealthBarDirect(data.health, this.player.maxHealth, healthDecreased);
                        }
                        
                        // Show damage effect if player took damage
                        if (healthDecreased) {
                            console.log(`Showing damage effect for health decrease (damage: ${data.damageAmount || healthDiff})`);
                            
                            // Use the actual damage amount if available, otherwise the calculated diff
                            const damageToShow = data.damageAmount || healthDiff;
                            this._showDamageEffect(damageToShow);
                            
                            // Add screen shake effect for more feedback
                            this._addScreenShake();
                        }
                        
                        // If health is low, show visual feedback (optional)
                        if (data.health < 30) {
                            // Could add screen flashing or audio cue here
                            console.log("WARNING: Player health is critically low!");
                        }
                    } else {
                        console.log(`Health was not included in the update`);
                    }
                } else {
                    console.log(`Update is for another player (ID: ${data.id}), ignoring`);
                }
                console.log(`%%%%%%%%%%%%%%%%%%%%%%%%%%%%%`);
            });
            
            // ENHANCED: Add backup handler for player damage to ensure health updates are processed
            this.socket.on('playerDamaged', (data) => {
                console.log(`Received playerDamaged event for player ${data.id} (damage: ${data.damageAmount})`);
                
                // Check if this is the local player
                if (this.player && this.socket && this.socket.id === data.id) {
                    console.log(`Local player ${data.id} received damage from source ${data.damageSource}`);
                    
                    // Check if this specific damage event has already been processed
                    const processedHealthUpdates = this.processedHealthUpdates || new Set();
                    const updateId = data.attackId || data.timestamp || Date.now();
                    
                    if (processedHealthUpdates.has(updateId)) {
                        console.log(`Skipping duplicate damage event ${updateId}`);
                        return;
                    } else {
                        // Mark this update as processed
                        processedHealthUpdates.add(updateId);
                        this.processedHealthUpdates = processedHealthUpdates;
                    }
                    
                    // Check if health data is included
                    if (data.health !== undefined) {
                        // Calculate how much health decreased
                        const oldHealth = this.player.health || 100;
                        const healthDecreased = oldHealth > data.health;
                        const healthDiff = healthDecreased ? oldHealth - data.health : 0;
                        
                        console.log(`Health change from damage event: ${oldHealth} -> ${data.health} (diff: ${healthDiff})`);
                        
                        // Update the player's health
                        this.player.health = data.health;
                        
                        // Update UI using any available method
                        if (this.uiManager) {
                            console.log(`Updating UI health bar from playerDamaged using this.uiManager`);
                            this.uiManager.updateHealth(data.health, this.player.maxHealth, healthDecreased);
                        } else if (window.uiManager) {
                            console.log(`Updating UI health bar from playerDamaged using window.uiManager`);
                            window.uiManager.updateHealth(data.health, this.player.maxHealth, healthDecreased);
                        } else {
                            console.warn(`No UI manager available for playerDamaged event, using direct DOM update`);
                            this._updateHealthBarDirect(data.health, this.player.maxHealth, healthDecreased);
                        }
                        
                        // Show damage effect if health decreased
                        if (healthDecreased) {
                            console.log(`Showing damage effect from playerDamaged event (damage: ${data.damageAmount})`);
                            const damageToShow = data.damageAmount || healthDiff;
                            this._showDamageEffect(damageToShow);
                            this._addScreenShake();
                        }
                    }
                }
            });
            
            // Wave events
            this.socket.on('waveStart', (data) => this._onWaveStart(data));
            this.socket.on('waveUpdate', (data) => this._onWaveUpdate(data));
            this.socket.on('waveEnd', (data) => this._onWaveEnd(data));
            this.socket.on('xpAward', (data) => this._onXpAward(data));
            
            // Add explicit player death event handler - CRITICAL for death state
            this.socket.on('playerDied', (data) => {
                console.log(`Received playerDied event for player ${data.id}`);
                
                // Check if this is the local player
                if (this.player && this.socket && this.socket.id === data.id) {
                    console.log(`Local player ${data.id} died. Killer: ${data.killedBy || 'unknown'}`);
                    
                    // Set health to 0 if not already
                    this.player.health = 0;
                    
                    // Trigger death sequence if player isn't already marked as dead
                    if (!this.player.isDead) {
                        this.onPlayerDeath(this.player);
                    }
                } else if (this.remotePlayersManager) {
                    // Handle remote player death
                    console.log(`Remote player ${data.id} died`);
                    this.remotePlayersManager.updatePlayerDeath(data.id, true);
                }
            });
            
            // Also add handler for backup health critical event
            this.socket.on('playerHealthCritical', (data) => {
                // Check if this is the local player
                if (this.player && this.socket && this.socket.id === data.id) {
                    console.log(`Local player health critical: ${data.health}/${data.maxHealth}`);
                    
                    // Update health value
                    this.player.health = data.health;
                    
                    // Show critical health effect (pulsing red vignette, heartbeat sound, etc.)
                    this._showCriticalHealthEffect();
                }
            });
            
            // Automatically request wave start after a delay
            setTimeout(() => {
                // Request wave start to ensure it begins (redundant with server auto-start)
                this.requestWaveStart();
            }, 8000); // 8 seconds after connecting
            
        } catch (error) {
            console.error('Failed to connect to server:', error);
        }
    }
    
    /**
     * Load game assets
     * @param {Function} progressCallback - Callback for loading progress
     * @returns {Promise} - Resolves when assets are loaded
     */
    async loadAssets(progressCallback) {
        console.log('Starting to load game assets...');
        
        try {
            // Start loading assets by calling the AssetManager's init method
            await this.assetManager.init();
            console.log('Asset loading completed successfully');
            
            // Initialize RemotePlayersManager only after assets are loaded
            if (this.socket && this.sceneManager && this.assetManager) {
                console.log('Initializing RemotePlayersManager with loaded assets');
                this.remotePlayersManager = new RemotePlayersManager(
                    this.sceneManager,
                    this.assetManager,
                    this.socket
                );
            } else {
                console.warn('Cannot initialize RemotePlayersManager: missing dependencies');
            }
            
            // Call the progress callback with 100% completion
            if (progressCallback) {
                progressCallback(100, 'All assets loaded');
                console.log('Progress callback called with 100%');
            } else {
                console.warn('No progress callback provided');
            }
            
            // Create an event listener for the loading progress updates
            const observer = new MutationObserver((mutations) => {
                const progressBar = document.querySelector('.loading-progress');
                if (progressBar && progressCallback) {
                    const width = progressBar.style.width || '0%';
                    const progress = parseInt(width);
                    const message = document.querySelector('.loading-text')?.textContent || 'Loading assets...';
                    progressCallback(progress, message);
                    console.log(`Loading progress: ${progress}% - ${message}`);
                }
            });
            
            // Start observing the progress bar for changes
            const progressBar = document.querySelector('.loading-progress');
            if (progressBar) {
                observer.observe(progressBar, { attributes: true, attributeFilter: ['style'] });
                console.log('Progress bar observer set up');
            } else {
                console.warn('Progress bar element not found for observation');
            }
        } catch (error) {
            console.error('Error during asset loading:', error);
            // Still call the callback with error message
            if (progressCallback) {
                progressCallback(0, 'Error loading assets');
            }
        }
    }
    
    /**
     * Set the player character
     * @param {string} playerType - Type of player character
     */
    setPlayer(playerType) {
        console.log(`Setting player character: ${playerType}`);
        
        if (!playerType) {
            console.error('No player type specified');
            return false;
        }
        
        // Store the player object
        this.player = playerType;
        
        // Set the scene reference on the player
        if (this.player && this.scene) {
            console.log('Setting scene on player');
            this.player.scene = this.scene;
            
            // Initialize the player
            console.log('Initializing player...');
            this.player.init().then(success => {
                if (!success) {
                    console.error('Failed to initialize player');
                }
            });
        } else {
            console.error('Cannot set scene on player - missing scene reference');
        }
        
        // Log player info for debugging
        console.log('Player object:', this.player);
        
        // Initialize movement controller with socket for syncing
        if (this.player && this.camera && this.scene) {
            // Only create movement controller if it doesn't exist
            if (!this.movementController) {
                console.log('Creating MovementController');
                this.movementController = new MovementController(
                    this.player,
                    this.camera,
                    this.scene,
                    { socket: this.socket }
                );
            }
            
            // Only create inventory manager if it doesn't exist
            if (!this.inventoryManager) {
                console.log('Creating InventoryManager');
                this.inventoryManager = new InventoryManager(
                    this.player,
                    this.assetManager
                );
                this.inventoryManager.init();
            } else {
                // If it exists, ensure it has the correct player reference
                this.inventoryManager.player = this.player;
                this.inventoryManager.unequipCurrentWeapon(); // Remove any existing weapons
            }
            
            // Only create combat manager if it doesn't exist
            if (!this.combatManager) {
                console.log('Creating CombatManager');
                const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
                this.combatManager = new CombatManager(
                    this.player,
                    this.inventoryManager,
                    this.sceneManager,
                    {
                        socket: this.socket,
                        isMobile: isMobile,
                        camera: this.camera
                    }
                );
                this.combatManager.init();
            } else {
                // If it exists, ensure it has the correct player reference
                this.combatManager.player = this.player;
                this.combatManager.inventoryManager = this.inventoryManager;
                // Also update the camera reference if needed
                if (this.camera && !this.combatManager.camera) {
                    this.combatManager.camera = this.camera;
                }
            }
            
            return true;
        } else {
            console.error('Cannot create controllers - missing dependencies:',
                { 
                    hasPlayer: !!this.player, 
                    hasCamera: !!this.camera, 
                    hasScene: !!this.scene 
                }
            );
            return false;
        }
    }
    
    /**
     * Initialize the game world
     * @returns {boolean} - True if the world was initialized successfully
     */
    async initWorld() {
        console.log('Initializing game world...');
        
        try {
            // Safety check - we need a valid scene
            if (!this.scene) {
                console.error('Cannot initialize world: scene is not available');
                return false;
            }
            
            // Safety check - we need a player and socket
            if (!this.player || !this.socket) {
                console.error('Cannot initialize world: player or socket not available');
                return false;
            }
            
            // Detect if we should use performance mode
            const usePerformanceMode = this._shouldUsePerformanceMode();
            console.log(`Performance mode: ${usePerformanceMode ? 'ENABLED' : 'DISABLED'}`);
            
            // Apply performance settings to scene manager if available
            if (this.sceneManager && typeof this.sceneManager.updateForDevice === 'function') {
                this.sceneManager.updateForDevice(
                    usePerformanceMode ? 'mobile' : 'desktop', 
                    {
                        shadows: !usePerformanceMode,
                        reduceTreeDetail: usePerformanceMode
                    }
                );
            }
            
            // Set up scene lighting if not already present
            if (!this.scene.getObjectByName('main-light')) {
                console.log('Adding lighting to scene...');
                
                // Add ambient light for general illumination
                const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
                ambientLight.name = 'ambient-light';
                this.scene.add(ambientLight);
                
                // Add directional light for shadows and definition
                const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
                directionalLight.position.set(10, 20, 10);
                directionalLight.name = 'main-light';
                this.scene.add(directionalLight);
                
                console.log('Scene lighting added');
            }
            
            // Create the dome environment if not already present
            if (this.sceneManager && typeof this.sceneManager.createEnvironment === 'function') {
                console.log('Creating dome environment...');
                this.sceneManager.createEnvironment(this.assetManager);
            } else {
                console.warn('Cannot create environment: SceneManager createEnvironment method not available');
            }
            
            // Create inventory manager if not already created
            if (!this.inventoryManager) {
                console.log('Creating inventory manager...');
                this.inventoryManager = new InventoryManager({
                    player: this.player,
                    socket: this.socket
                });
            }
            
            // Create combat manager if not already created
            if (!this.combatManager) {
                console.log('Creating combat manager...');
                this.combatManager = new CombatManager({
                    player: this.player,
                    socket: this.socket
                });
            }
            
            // Create enemy renderer
            console.log('Creating enemy renderer...');
            this.enemyRenderer = new EnemyRenderer({
                scene: this.scene,
                assetManager: this.assetManager
            });
            
            // Verify scene was set in the renderer
            if (!this.enemyRenderer.scene) {
                console.error('Failed to set scene in enemy renderer');
                return false;
            }
            
            // Wait for enemy model preloading to complete
            console.log('Waiting for enemy models to preload...');
            await this.enemyRenderer.initializeModelLoader();
            
            // Create ProjectileManager
            console.log('Creating projectile manager...');
            this.projectileManager = new ProjectileManager({
                scene: this.scene,
                combatManager: this.combatManager,
                socket: this.socket
            });

            // Initialize the projectile manager
            if (this.projectileManager && this.scene) {
                this.projectileManager.initialize(this.scene);
            }
            
            // Check if projectile manager was created successfully
            if (!this.projectileManager) {
                console.warn('Failed to create projectile manager!');
            }
            
            // Pass projectileManager to enemyManager
            console.log('Creating enemy manager...');
            this.enemyManager = new EnemyManager({
                socket: this.socket,
                renderer: this.enemyRenderer,
                projectileManager: this.projectileManager
            });

            // Register the enemy manager's socket handlers
            this.enemyManager.registerSocketHandlers();

            // Set up enemy socket events
            this._setupEnemySocketEvents();
            
            // Process any queued enemy events
            this._processPendingEvents();
            
            // Set the game as running
            this.isRunning = true;
            
            // Start the clock - this is critical for the game loop
            this.clock.start();
            
            // Start the render loop if it exists but isn't running
            if (this.renderLoop && !this.renderLoop.isActive()) {
                console.log('Starting render loop...');
                this.renderLoop.start();
            } else if (!this.renderLoop) {
                console.error('Render loop not initialized');
                return false;
            }
            
            // Explicitly request all existing enemies from the server
            this.requestEnemySync();
            
            // Create VibeversePortalManager
            console.log('Creating vibeverse portal manager...');
            this.vibeversePortalManager = new VibeversePortalManager(
                this.scene,
                this.player,
                this.renderLoop
            );

            // Initialize the portal manager
            this.vibeversePortalManager.init().then(success => {
                if (!success) {
                    console.warn('Failed to initialize vibeverse portal');
                }
            });
            
            console.log('World initialized successfully');
            return true;
        } catch (error) {
            console.error('Error initializing world:', error);
            return false;
        }
    }
    
    /**
     * Determine if performance mode should be enabled
     * @private
     * @returns {boolean} True if performance mode should be used
     */
    _shouldUsePerformanceMode() {
        // Get stored preference if available
        const storedPreference = localStorage.getItem('performanceMode');
        if (storedPreference !== null) {
            return storedPreference === 'true';
        }
        
        // Default to performance mode ON for better performance
        return true;
    }
    
    /**
     * Toggle performance mode on/off
     * @returns {boolean} New performance mode state
     */
    togglePerformanceMode() {
        const currentMode = this._shouldUsePerformanceMode();
        const newMode = !currentMode;
        
        // Store the preference
        localStorage.setItem('performanceMode', newMode.toString());
        
        // Update scene manager settings
        if (this.sceneManager && typeof this.sceneManager.updateForDevice === 'function') {
            this.sceneManager.updateForDevice(
                newMode ? 'mobile' : 'desktop', 
                {
                    shadows: !newMode,
                    reduceTreeDetail: newMode
                }
            );
        }
        
        console.log(`Performance mode ${newMode ? 'enabled' : 'disabled'}`);
        return newMode;
    }
    
    /**
     * Set up socket event handlers for enemy synchronization
     * @private
     */
    _setupEnemySocketEvents() {
        if (!this.socket) {
            console.error('Socket not initialized in Game._setupEnemySocketEvents');
            return;
        }

        // Handle batched projectile updates
        this.socket.on('update-projectiles', (updates) => {
            if (!this.projectileManager) return;
            
            // Process all updates in a single batch
            updates.forEach(update => {
                const projectile = this.projectileManager.getProjectile(update.id);
                if (projectile && projectile.mesh) {
                    projectile.mesh.position.set(
                        update.position.x,
                        update.position.y,
                        update.position.z
                    );
                }
            });
        });

        // Handle enemy attack events
        this.socket.on('enemyAttack', (data) => {
            if (!this.enemyManager || !this.projectileManager) return;
            
            const enemy = this.enemyManager.getEnemy(data.id);
            if (enemy) {
                // Find target position
                let targetPos;
                if (this.player && this.socket && data.targetId === this.socket.id) {
                    targetPos = this.player.position;
                } else {
                    targetPos = this.remotePlayers && this.remotePlayers.players[data.targetId] ? 
                        this.remotePlayers.players[data.targetId].position : 
                        { 
                            x: enemy.position.x, 
                            y: enemy.position.y, 
                            z: enemy.position.z + 10 
                        };
                }
                
                // Calculate direction vector
                const direction = {
                    x: targetPos.x - enemy.position.x,
                    y: targetPos.y - enemy.position.y,
                    z: targetPos.z - enemy.position.z
                };
                
                // Normalize direction vector
                const length = Math.sqrt(
                    direction.x * direction.x + 
                    direction.y * direction.y + 
                    direction.z * direction.z
                );
                
                if (length > 0) {
                    direction.x /= length;
                    direction.y /= length;
                    direction.z /= length;
                    
                    const isMage = data.type === 'mage';
                    const projectileId = `proj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                    
                    // Create projectile with optimized properties
                    const projectile = {
                        id: projectileId,
                        position: {
                            x: enemy.position.x + direction.x * 0.5,
                            y: enemy.position.y + 1.0,
                            z: enemy.position.z + direction.z * 0.5
                        },
                        velocity: {
                            x: direction.x * (isMage ? 10 : 15),
                            y: direction.y * (isMage ? 10 : 15),
                            z: direction.z * (isMage ? 10 : 15)
                        },
                        type: isMage ? 'magic' : 'arrow',
                        color: isMage ? 0x00bbff : 0xff3300,
                        damage: data.damage || (isMage ? 25 : 15),
                        sourceId: data.id,
                        targetId: data.targetId,
                        lifetime: isMage ? 3 : 2,
                        isEnemyProjectile: true
                    };
                    
                    // Add projectile to manager
                    this.projectileManager.addProjectile(projectile);
                }
            }
        });

        if (!this.enemyManager || !this.enemyRenderer) {
            console.warn("Cannot setup enemy socket events: Enemy systems not initialized");
            return;
        }

        console.log('Setting up enemy socket events');
        
        // Queue to store enemy spawn events when system is not ready
        if (!this._enemyEventQueue) {
            this._enemyEventQueue = [];
            console.log('Created enemy event queue');
        }

        // Listen for enemy spawn events from the server
        this.socket.on('enemy:spawn', (enemyData) => {
            const timestamp = new Date().toISOString();
            console.log(`[${timestamp}] RECEIVED enemy:spawn event for ${enemyData.id} (type: ${enemyData.type})`);
            
            try {
                // Check if enemy system is ready
                if (!this.enemyManager || !this.enemyRenderer) {
                    console.warn(`[${timestamp}] Enemy system not ready, queueing spawn event for ${enemyData.id}`);
                    this._enemyEventQueue.push({ event: 'enemy:spawn', data: enemyData });
                    return;
                }

                // Check if we already have this enemy
                if (this.enemyManager.getEnemy(enemyData.id)) {
                    console.log(`[${timestamp}] Enemy ${enemyData.id} already exists, ignoring spawn event`);
                    return;
                }

                console.log(`[${timestamp}] PROCESSING enemy:spawn for ${enemyData.id} at position:`, enemyData.position);
                
                // Create the enemy
                const enemy = this.enemyManager.createEnemy(enemyData);

                if (enemy) {
                    console.log(`[${timestamp}] Enemy ${enemyData.id} created successfully`);
                    
                    // Register the enemy with the combat manager if available
                    if (this.combatManager) {
                        console.log(`[${timestamp}] Registering enemy ${enemyData.id} with combat manager`);
                        this.combatManager.registerEnemy(enemyData.id, enemy);
                    } else {
                        console.warn(`[${timestamp}] Cannot register enemy with combat manager: combat manager not initialized`);
                    }
                } else {
                    console.error(`[${timestamp}] Failed to create enemy ${enemyData.id}`);
                }
            } catch (error) {
                console.error(`[${timestamp}] Error handling enemy spawn event:`, error);
            }
        });

        // Listen for projectile spawn events
        this.socket.on('projectileSpawn', (data) => {
            console.log('%c⚡ Projectile spawn event received:', 'background: #3498db; color: white; padding: 2px 5px; border-radius: 3px;', data);
            if (this.projectileManager) {
                this.projectileManager.addProjectile(data);
            } else {
                console.warn('Cannot handle projectile: ProjectileManager not available');
                
                // If ProjectileManager not available, store pending projectiles for later processing
                if (!this._pendingProjectiles) {
                    this._pendingProjectiles = [];
                }
                this._pendingProjectiles.push(data);
                console.log('Stored projectile data for later processing');
            }
        });

        // Listen for enemy update events
        this.socket.on('enemy:update', (enemyData) => {
            try {
                // Skip detailed logging for frequent updates to avoid console spam
                // Only log occasionally (1% of updates)
                const shouldLog = Math.random() < 0.01;
                
                if (shouldLog) {
                    console.log(`Received enemy:update event for ID: ${enemyData.id}`, 
                        { position: enemyData.position, health: enemyData.health });
                }
                
                // Validate we have required components
                if (!this.enemyManager) {
                    if (shouldLog) {
                        console.warn('Enemy update event received but enemy system not ready');
                    }
                    return;
                }
                
                // Store update data for next game update cycle
                this.enemyManager.serverEnemyData[enemyData.id] = enemyData;
            } catch (error) {
                console.error('Error handling enemy:update event:', error);
            }
        });

        // Also listen for the regular 'enemyUpdate' event (for compatibility)
        this.socket.on('enemyUpdate', (enemyData) => {
            try {
                // Skip detailed logging for frequent updates
                const shouldLog = Math.random() < 0.01;
                
                if (shouldLog) {
                    console.log(`Received enemyUpdate event for ID: ${enemyData.id}`);
                }
                
                // Validate we have required components
                if (!this.enemyManager) {
                    if (shouldLog) {
                        console.warn('Enemy update event received but enemy system not ready');
                    }
                    return;
                }
                
                // Store update data for next game update cycle
                this.enemyManager.serverEnemyData[enemyData.id] = enemyData;
            } catch (error) {
                console.error('Error handling enemyUpdate event:', error);
            }
        });

        // Listen for enemy destroy events
        this.socket.on('enemy:destroy', (enemyId) => {
            try {
                console.log('Received enemy:destroy event for ID:', enemyId);
                
                // Validate we have required components
                if (!this.enemyManager) {
                    console.warn('Enemy destroy event received but enemy system not ready');
                    return;
                }
                
                // Remove enemy from manager
                this.enemyManager.removeEnemy(enemyId);
                
                // Clean up any stored server data
                delete this.enemyManager.serverEnemyData[enemyId];
            } catch (error) {
                console.error('Error handling enemy:destroy event:', error);
            }
        });

        // Listen for enemy debug info
        this.socket.on('enemyDebugInfo', (debugData) => {
            try {
                console.log('======= ENEMY DEBUG INFO FROM SERVER =======');
                console.log(`Server time: ${new Date(debugData.serverTime).toISOString()}`);
                console.log(`Total enemies: ${debugData.totalEnemies}`);
                
                if (debugData.error) {
                    console.error('Server error:', debugData.error);
                    return;
                }
                
                // Log detailed enemy information
                if (debugData.enemies && debugData.enemies.length > 0) {
                    console.log('\nEnemy details:');
                    debugData.enemies.forEach(enemy => {
                        const lastUpdated = enemy.lastUpdated ? new Date(enemy.lastUpdated).toISOString() : 'N/A';
                        const createdAt = enemy.createdAt ? new Date(enemy.createdAt).toISOString() : 'N/A';
                        
                        console.log(`- ID: ${enemy.id}`);
                        console.log(`  Type: ${enemy.type}, Level: ${enemy.level}`);
                        console.log(`  Health: ${enemy.health}/${enemy.maxHealth}`);
                        console.log(`  Position: (${enemy.position.x.toFixed(2)}, ${enemy.position.y.toFixed(2)}, ${enemy.position.z.toFixed(2)})`);
                        console.log(`  Active: ${enemy.isActive}, Created: ${createdAt}, Last Updated: ${lastUpdated}`);
                        console.log(`  Local instance exists: ${this.enemyManager ? !!this.enemyManager.getEnemy(enemy.id) : 'Enemy manager not ready'}`);
                    });
                }
                
                // Log server info
                if (debugData.serverInfo) {
                    console.log('\nServer info:');
                    console.log(`Connected clients: ${debugData.serverInfo.connectedClients}`);
                    console.log(`Server uptime: ${(debugData.serverInfo.uptime / 60).toFixed(2)} minutes`);
                    console.log(`Memory usage: ${(debugData.serverInfo.memory.heapUsed / 1024 / 1024).toFixed(2)}MB / ${(debugData.serverInfo.memory.heapTotal / 1024 / 1024).toFixed(2)}MB`);
                }
                
                // Compare with local state
                if (this.enemyManager) {
                    // Get local enemies - check which method is available
                    let localEnemies = {}; 
                    if (typeof this.enemyManager.getAllEnemies === 'function') {
                        localEnemies = this.enemyManager.getAllEnemies();
                    } else if (typeof this.enemyManager.getEnemies === 'function') {
                        localEnemies = this.enemyManager.getEnemies();
                    } else {
                        // Fallback if neither method exists
                        localEnemies = this.enemyManager.enemies || {};
                    }
                    
                    const localEnemyIds = Object.keys(localEnemies);
                    const serverEnemyIds = debugData.enemies ? debugData.enemies.map(e => e.id) : [];
                    
                    const missingLocally = serverEnemyIds.filter(id => !localEnemyIds.includes(id));
                    const missingOnServer = localEnemyIds.filter(id => !serverEnemyIds.includes(id));
                    
                    console.log('\nSynchronization status:');
                    console.log(`Local enemies: ${localEnemyIds.length}, Server enemies: ${serverEnemyIds.length}`);
                    
                    if (missingLocally.length > 0) {
                        console.warn(`Missing enemies locally: ${missingLocally.length}`);
                        missingLocally.forEach(id => console.warn(`- Missing locally: ${id}`));
                    } else {
                        console.log('All server enemies exist locally.');
                    }
                    
                    if (missingOnServer.length > 0) {
                        console.warn(`Extra enemies locally: ${missingOnServer.length}`);
                        missingOnServer.forEach(id => console.warn(`- Missing on server: ${id}`));
                    } else {
                        console.log('No extra enemies locally.');
                    }
                } else {
                    console.warn('Cannot compare with local state: Enemy manager not ready');
                }
                
                console.log('============================================');
            } catch (error) {
                console.error('Error handling enemy debug info:', error);
            }
        });

        console.log('Enemy socket events setup complete');
    }
    
    /**
     * Regular update method called from render loop
     */
    update() {
        // Check if game is running
        if (!this.isRunning) return;
        
        try {
        // Calculate delta time
        this.deltaTime = this.clock.getDelta();
        
        // CRITICAL: Make sure the global players object exists and includes the local player
        // This ensures enemies can find the player when targeting
        if (!window.game) {
            window.game = this;
        }
        
        // Create or update the players object for enemy targeting
        if (!window.game.players) {
            window.game.players = {};
        }
        
        // Make sure the local player is always in the players object
        if (this.player && this.socket) {
            window.game.players[this.socket.id] = this.player;
        }
        
        // Update movement
        if (this.movementController) {
            this.movementController.update(this.deltaTime);
        }
        
        // Update remote players
        if (this.remotePlayersManager) {
            this.remotePlayersManager.update(this.deltaTime);
        }
        
        // Update inventory
        if (this.inventoryManager) {
            this.inventoryManager.update(this.deltaTime);
        }
        
        // Update combat
        if (this.combatManager) {
            this.combatManager.update(this.deltaTime);
        }
            
            // Update enemies - pass the current players map
            if (this.enemyManager) {
                const players = {};
                
                // Add the local player
                if (this.player && this.socket) {
                    players[this.socket.id] = this.player;
                }
                
                // Add remote players if available
                if (this.remotePlayersManager) {
                    // Changed from getPlayerStates to getPlayers
                    const remotePlayers = this.remotePlayersManager.getPlayers 
                        ? this.remotePlayersManager.getPlayers() 
                        : this.remotePlayersManager.remotePlayers;
                    
                    if (remotePlayers) {
                        if (remotePlayers instanceof Map) {
                            // Convert Map to object
                            remotePlayers.forEach((player, id) => {
                                players[id] = player;
                            });
                        } else {
                            // Assume it's already an object
                            Object.assign(players, remotePlayers);
                        }
                    }
                }
                
                // Only update enemies if we have at least one player
                if (Object.keys(players).length > 0) {
                    // Don't pass deltaTime as first param anymore
                    this.enemyManager.update(players);
                }
                
                // Update enemy health bars to maintain consistent size
                if (this.enemyRenderer && this.camera) {
                    this.enemyRenderer.updateHealthBars(this.camera);
                }
            }
        
        // Update player
        if (this.player) {
            this.player.update(this.deltaTime);
        }
        
        // Update all entities
        for (const entity of this.entities) {
            if (entity.update) {
                entity.update(this.deltaTime);
            }
        }
        
        // Update scene manager
        if (this.sceneManager) {
            this.sceneManager.update(this.deltaTime);
        }
            
            // Check for and process any pending events
            // Do this occasionally to avoid constant checking
            if (Math.random() < 0.05) { // 5% chance per frame
                this._processPendingEvents();
            }
        
        // Attempt to repair animations if needed (done occasionally)
        if (Math.random() < 0.01) { // 1% chance per frame
            this._checkAndRepairAnimations();
            }

        // Update projectiles
        if (this.projectileManager) {
            // Register local player for collision detection
            if (this.player && this.socket) {
                this.projectileManager.registerPlayer(
                    this.socket.id, 
                    {
                        id: this.socket.id,
                        position: this.player.position
                    }
                );
            }
            
            // Register remote players for collision detection
            if (this.remotePlayersManager) {
                const remotePlayers = this.remotePlayersManager.getPlayers 
                    ? this.remotePlayersManager.getPlayers() 
                    : this.remotePlayersManager.remotePlayers;
                
                if (remotePlayers) {
                    if (remotePlayers instanceof Map) {
                        // If it's a Map
                        remotePlayers.forEach((player, id) => {
                            this.projectileManager.registerPlayer(id, player);
                        });
                    } else {
                        // If it's an object
                        Object.entries(remotePlayers).forEach(([id, player]) => {
                            this.projectileManager.registerPlayer(id, player);
                        });
                    }
                }
            }
            
            // Update projectiles with deltaTime
            this.projectileManager.update(this.deltaTime);
        }

        // Update the enemyRenderer too
        if (this.enemyRenderer) {
            this.enemyRenderer.update(this.deltaTime);
        }
        } catch (error) {
            console.error('Error in update loop:', error);
        }
    }
    
    /**
     * Check for and repair any broken animations
     * @private
     */
    _checkAndRepairAnimations() {
        // Check player animations
        if (this.player && !this.player.animation) {
            console.log('Attempting to repair local player animations');
            this.player.setAnimation('idle');
        }
        
        // Check remote player animations
        if (this.remotePlayersManager) {
            this.remotePlayersManager.remotePlayers.forEach((player, id) => {
                let needsRepair = false;
                
                // Check for missing animation system
                if (!player.animation || !player.animation.mixer) {
                    console.log(`Attempting to repair missing animation system for remote player ${id}`);
                    needsRepair = true;
                } 
                // Check for missing current animation
                else if (player.modelMesh && !player.currentAnimation) {
                    console.log(`Setting default animation for remote player ${id} with missing currentAnimation`);
                    needsRepair = true;
                }
                // Check for T-pose by examining animation progress
                else if (player.modelMesh && player._checkForTpose && !player._checkForTpose()) {
                    console.log(`Detected T-pose on remote player ${id}, repairing animation`);
                    needsRepair = true;
                }
                
                // Apply repairs if needed
                if (needsRepair) {
                    // Reset animation system
                    player.setAnimation(player.isMoving ? 'run' : 'idle');
                    
                    // Force animation to start playing
                    if (player.animation && player.animation.mixer) {
                        // Multiple updates to kick-start the animation
                        for (let i = 0; i < 5; i++) {
                            player.animation.mixer.update(0.016);
                        }
                    }
                }
            });
        }
    }
    
    /**
     * Handle window resize
     */
    onWindowResize() {
        if (!this.camera || !this.renderer) return;
        
        // Update camera aspect ratio
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        
        // Update renderer size
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }
    
    /**
     * Add entity to the game
     * @param {Object} entity - Entity to add
     */
    addEntity(entity) {
        if (entity.init) {
            entity.init(this.scene, this.assetManager);
        }
        this.entities.push(entity);
    }
    
    /**
     * Remove entity from the game
     * @param {Object} entity - Entity to remove
     */
    removeEntity(entity) {
        const index = this.entities.indexOf(entity);
        if (index !== -1) {
            this.entities.splice(index, 1);
            
            if (entity.dispose) {
                entity.dispose(this.scene);
            }
        }
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        console.log('Disposing game resources...');
        
        // Remove event listeners
        window.removeEventListener('resize', this.onWindowResize);
        
        // Dispose enemy systems
        if (this.enemyRenderer) {
            this.enemyRenderer.dispose();
            this.enemyRenderer = null;
        }
        
        if (this.enemyManager) {
            this.enemyManager.dispose();
            this.enemyManager = null;
        }
        
        // Dispose remote players manager
        if (this.remotePlayersManager) {
            this.remotePlayersManager.dispose();
            this.remotePlayersManager = null;
        }
        
        // Dispose combat manager
        if (this.combatManager) {
            this.combatManager.dispose();
            this.combatManager = null;
        }
        
        // Dispose inventory manager
        if (this.inventoryManager) {
            this.inventoryManager.dispose();
            this.inventoryManager = null;
        }
        
        // Disconnect socket
        if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
        }
        
        // Dispose movement controller
        if (this.movementController) {
            // No specific disposal needed for movement controller
            this.movementController = null;
        }
        
        // Dispose player
        if (this.player) {
            this.player.dispose();
            this.player = null;
        }
        
        // Dispose all entities
        for (const entity of this.entities) {
            if (entity.dispose) {
                entity.dispose();
            }
        }
        this.entities = [];
        
        // Dispose managers
        if (this.sceneManager) {
            this.sceneManager.dispose();
            this.sceneManager = null;
        }
        
        if (this.assetManager) {
            this.assetManager.dispose();
            this.assetManager = null;
        }
        
        // Dispose renderer
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer = null;
        }
        
        console.log('Game disposed');
    }
    
    /**
     * Handle mouse movement for player rotation
     * @param {number} mouseX - Mouse X coordinate relative to canvas
     * @param {number} mouseY - Mouse Y coordinate relative to canvas
     */
    handleMouseMove(mouseX, mouseY) {
        // If we have a movement controller, let it handle the mouse movement
        if (this.movementController) {
            // Convert canvas coordinates to normalized device coordinates
            const normalizedX = (mouseX / this.renderer.domElement.clientWidth) * 2 - 1;
            const normalizedY = -(mouseY / this.renderer.domElement.clientHeight) * 2 + 1;
            
            // Create a mock event object with client coordinates
            const mockEvent = {
                clientX: mouseX + this.renderer.domElement.getBoundingClientRect().left,
                clientY: mouseY + this.renderer.domElement.getBoundingClientRect().top
            };
            
            // Pass to movement controller
            this.movementController._handleMouseMove(mockEvent);
        }
    }
    
    /**
     * Send guest login information to the server
     * @private
     */
    _sendGuestLogin() {
        if (!this.socket || !this.connected) {
            console.warn('Cannot send guest login: not connected to server');
            return;
        }
        
        // Create a username if one doesn't exist
        if (!this.username) {
            this.username = 'Guest' + Math.floor(Math.random() * 1000);
        }
        
        console.log(`Sending guest login with username: ${this.username}`);
        
        // Use 'guestLogin' to match server expectation
        this.socket.emit('guestLogin', { 
            username: this.username 
        });
        
        // Handle login success
        this.socket.on('login-success', (data) => {
            console.log(`Login successful. Logged in as ${data.username}`);
            this.username = data.username;
            
            // We don't create RemotePlayersManager here anymore, 
            // it will be created after assets are loaded
        });
    }
    
    /**
     * Try a direct connection to the server if the initial connection fails
     * @private
     */
    _tryDirectConnection() {
        this.directConnectionAttempted = true;
        console.log('Trying direct connection to server...');
        
        // Disconnect any existing socket
        if (this.socket) {
            this.socket.disconnect();
        }
        
        // Attempt a connection using the current protocol (http or https)
        // This ensures we use wss:// for https pages
        const protocol = window.location.protocol;
        const serverUrl = `${protocol}//${window.location.host}`;
        console.log(`Attempting direct connection to ${serverUrl}`);
        
        this.socket = io(serverUrl, {
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            transports: ['websocket'],
            secure: protocol === 'https:'
        });
        
        // Re-attach event handlers
        this.socket.on('connect', () => {
            console.log('Connected to server through direct connection');
            this.connected = true;
            
            // Update status indicator
            const statusIndicator = document.getElementById('connection-status');
            if (statusIndicator) {
                statusIndicator.classList.remove('disconnected');
                statusIndicator.classList.add('connected');
                statusIndicator.textContent = 'Connected';
            }
            
            // Send login information after a short delay to ensure the connection is stable
            setTimeout(() => this._sendGuestLogin(), 500);
        });
        
        // Handle other events
        this.socket.on('disconnect', () => {
            console.log('Disconnected from direct connection');
            this.connected = false;
            
            const statusIndicator = document.getElementById('connection-status');
            if (statusIndicator) {
                statusIndicator.classList.remove('connected');
                statusIndicator.classList.add('disconnected');
                statusIndicator.textContent = 'Disconnected';
            }
        });
        
        this.socket.on('connect_error', (error) => {
            console.error('Direct connection error:', error);
            
            const statusIndicator = document.getElementById('connection-status');
            if (statusIndicator) {
                statusIndicator.classList.remove('connected');
                statusIndicator.classList.add('disconnected');
                statusIndicator.textContent = 'Disconnected';
            }
        });
    }
    
    /**
     * Process any pending events for systems that are now ready
     * @private
     */
    _processPendingEvents() {
        if (!this._enemyEventQueue || this._enemyEventQueue.length === 0) {
            return; // No events to process
        }

        if (!this.enemyManager || !this.enemyRenderer) {
            console.warn('Cannot process pending events: Enemy systems not ready');
            return;
        }

        const timestamp = new Date().toISOString();
        console.log(`[${timestamp}] Processing ${this._enemyEventQueue.length} pending enemy events`);

        // Make a copy of the queue to avoid issues during processing
        const events = [...this._enemyEventQueue];
        
        // Clear the queue first to prevent reprocessing if there's an error
        this._enemyEventQueue = [];

        // Process each event
        for (const eventData of events) {
            try {
                console.log(`[${timestamp}] Processing queued event: ${eventData.event} for enemy ${eventData.data.id}`);
                
                switch (eventData.event) {
                    case 'enemy:spawn':
                        // Check if we already have this enemy (might have been created since queueing)
                        if (this.enemyManager.getEnemy(eventData.data.id)) {
                            console.log(`[${timestamp}] Enemy ${eventData.data.id} already exists, skipping queued spawn`);
                            continue;
                        }
                        
                        console.log(`[${timestamp}] Creating enemy from queued spawn event:`, eventData.data);
                        
                        // Create the enemy
                        const enemy = this.enemyManager.createEnemy(eventData.data);
                        
                        if (enemy) {
                            console.log(`[${timestamp}] Created enemy ${enemy.id} from queue successfully`);
                            
                            // Register the enemy with the combat manager if available
                            if (this.combatManager) {
                                console.log(`[${timestamp}] Registering enemy ${enemy.id} with combat manager from queue`);
                                this.combatManager.registerEnemy(enemy.id, enemy);
                            } else {
                                console.warn(`[${timestamp}] Cannot register enemy with combat manager: combat manager not initialized`);
                            }
                        } else {
                            console.error(`[${timestamp}] Failed to create enemy from queue:`, eventData.data);
                        }
                        break;
                        
                    case 'enemy:update':
                        // Store update data for next game update cycle
                        this.enemyManager.serverEnemyData[eventData.data.id] = eventData.data;
                        break;
                        
                    case 'enemy:destroy':
                        // Remove the enemy
                        this.enemyManager.removeEnemy(eventData.data);
                        
                        // Clean up any stored server data
                        delete this.enemyManager.serverEnemyData[eventData.data];
                        break;
                        
                    default:
                        console.warn(`[${timestamp}] Unknown event type in queue:`, eventData.event);
                }
            } catch (error) {
                console.error(`[${timestamp}] Error processing queued event:`, error, eventData);
            }
        }

        console.log(`[${timestamp}] Finished processing pending events`);
    }
    
    /**
     * Request synchronization of all existing enemies from the server
     */
    requestEnemySync() {
        if (!this.socket) {
            console.warn("Cannot request enemy sync: Socket connection not established");
            return;
        }

        console.log("Requesting explicit enemy synchronization from server");
        this.socket.emit('requestEnemySync');
        
        // Log client state for debugging
        const clientState = {
            hasEnemyManager: !!this.enemyManager,
            enemyCount: this.enemyManager ? 
                (typeof this.enemyManager.getEnemyCount === 'function' ? 
                    this.enemyManager.getEnemyCount() : 
                    Object.keys(this.enemyManager.enemies || {}).length) : 0,
            hasEnemyRenderer: !!this.enemyRenderer,
            hasScene: !!this.scene,
            socketConnected: !!this.socket?.connected,
            pendingEventsCount: this._enemyEventQueue ? this._enemyEventQueue.length : 0
        };
        
        console.log("Client state at sync request:", clientState);
    }
    
    /**
     * Request detailed enemy state information from the server
     * This is a debug method to help identify synchronization issues
     */
    requestEnemyDebugInfo() {
        if (!this.socket) {
            console.warn("Cannot request enemy debug info: Socket connection not established");
            return;
        }

        console.log("Requesting enemy debug information from server");
        this.socket.emit('requestEnemyDebugInfo');
    }
    
    /**
     * Shows visual effect when player takes damage
     * @param {number} amount - Amount of damage taken
     * @private
     */
    _showDamageEffect(amount) {
        // Create damage overlay if not exists
        let overlay = document.getElementById('damage-overlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'damage-overlay';
            overlay.style.position = 'fixed';
            overlay.style.top = '0';
            overlay.style.left = '0';
            overlay.style.width = '100%';
            overlay.style.height = '100%';
            overlay.style.backgroundColor = 'rgba(255,0,0,0.3)';
            overlay.style.pointerEvents = 'none';
            overlay.style.zIndex = '1000';
            overlay.style.opacity = '0';
            document.body.appendChild(overlay);
        }
        
        // Calculate opacity based on damage amount (more damage = more red)
        const intensity = Math.min(0.7, amount / 25); // Cap at 0.7 opacity
        
        // Reset animation by removing and re-adding the element
        if (overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
        }
        document.body.appendChild(overlay);
        
        // Show damage effect
        overlay.style.opacity = intensity.toString();
        
        // Fade out
        setTimeout(() => {
            overlay.style.transition = 'opacity 0.5s ease-out';
            overlay.style.opacity = '0';
        }, 100);
    }
    
    /**
     * Shows critical health effect when player health is very low
     * @private
     */
    _showCriticalHealthEffect() {
        // Create critical health overlay if it doesn't exist
        let criticalOverlay = document.getElementById('critical-health-overlay');
        if (!criticalOverlay) {
            criticalOverlay = document.createElement('div');
            criticalOverlay.id = 'critical-health-overlay';
            criticalOverlay.style.position = 'fixed';
            criticalOverlay.style.top = '0';
            criticalOverlay.style.left = '0';
            criticalOverlay.style.width = '100%';
            criticalOverlay.style.height = '100%';
            criticalOverlay.style.boxShadow = 'inset 0 0 50px 10px rgba(255,0,0,0.5)';
            criticalOverlay.style.pointerEvents = 'none';
            criticalOverlay.style.zIndex = '999';
            criticalOverlay.style.opacity = '0';
            document.body.appendChild(criticalOverlay);
        }
        
        // Clear any existing animation
        criticalOverlay.style.animation = 'none';
        
        // Add pulsing animation
        setTimeout(() => {
            criticalOverlay.style.animation = 'pulse-critical 1.5s ease-in-out infinite';
            
            // Add the CSS animation if it doesn't exist
            if (!document.getElementById('critical-health-keyframes')) {
                const style = document.createElement('style');
                style.id = 'critical-health-keyframes';
                style.textContent = `
                    @keyframes pulse-critical {
                        0% { opacity: 0.3; }
                        50% { opacity: 0.7; }
                        100% { opacity: 0.3; }
                    }
                `;
                document.head.appendChild(style);
            }
            
            // Play heartbeat sound if available
            if (this.assetManager && this.assetManager.getSound) {
                const heartbeatSound = this.assetManager.getSound('heartbeat');
                if (heartbeatSound) {
                    heartbeatSound.play();
                }
            }
        }, 10);
        
        // Clear effect after 5 seconds if health is restored
        // (the effect will restart if _showCriticalHealthEffect is called again)
                    setTimeout(() => {
            if (this.player && this.player.health > 25) {
                criticalOverlay.style.animation = 'none';
                criticalOverlay.style.opacity = '0';
            }
        }, 5000);
    }
    
    /**
     * Adds screen shake effect
     * @private
     */
    _addScreenShake() {
        const canvas = document.querySelector('canvas');
        if (!canvas) return;
        
        // Early return if we're already shaking to avoid multiple overlapping effects
        if (this._isShaking) return;
        this._isShaking = true;
        
        // Save original position/transform
        const originalTransform = canvas.style.transform || '';
        
        // Use more efficient properties - fewer calculations
        const intensity = 2; // Reduced intensity
        const duration = 150; // Shorter duration
        const frequency = 100; // Fewer updates for better performance
        const startTime = Date.now();
        
        // Use CSS transform for better performance
        const shakeFrame = () => {
            const elapsed = Date.now() - startTime;
            
            if (elapsed >= duration) {
                // Reset to original position
                canvas.style.transform = originalTransform;
                this._isShaking = false;
                return;
            }
            
            // Reduce intensity as we progress (linear falloff)
            const progress = elapsed / duration;
            const currentIntensity = intensity * (1 - progress);
            
            // Calculate random offset with reduced intensity over time
            const offsetX = (Math.random() * 2 - 1) * currentIntensity;
            const offsetY = (Math.random() * 2 - 1) * currentIntensity;
            
            // Apply shake using transform3d for hardware acceleration
            canvas.style.transform = `${originalTransform} translate3d(${offsetX}px, ${offsetY}px, 0)`;
            
            // Request next frame with throttling to reduce CPU usage
            setTimeout(() => requestAnimationFrame(shakeFrame), frequency);
        };
        
        // Start the animation
        requestAnimationFrame(shakeFrame);
    }

    /**
     * Handle wave start event
     * @param {Object} data - Wave start data
     * @private
     */
    _onWaveStart(data) {
        console.log(`%cWave ${data.waveNumber} started with ${data.enemyCount} enemies`, 'color: blue; font-weight: bold');
        
        // Update UI with wave information
        if (this.uiManager) {
            // Ensure UI manager knows we're in the game screen
            this.uiManager.activeScreen = 'game';
            
            // Update wave info display
            this.uiManager.updateWaveInfo(data.waveNumber, data.enemyCount);
            
            // Show wave start notification
            this._showWaveStartNotification(data.waveNumber);
        }
    }

    /**
     * Handle wave update event
     * @param {Object} data - Wave update data
     * @private
     */
    _onWaveUpdate(data) {
        console.log(`%cWave update: ${data.enemiesRemaining} enemies remaining in wave ${data.waveNumber}`, 'color: gray');
        
        // Update UI with wave information
        if (this.uiManager) {
            // Ensure UI manager knows we're in the game screen
            this.uiManager.activeScreen = 'game';
            
            // Update wave info
            this.uiManager.updateWaveInfo(data.waveNumber, data.enemiesRemaining);
        }
    }

    /**
     * Handle wave end event
     * @param {Object} data - Wave end data
     * @private
     */
    _onWaveEnd(data) {
        console.log(`%cWave ${data.waveNumber} completed, next wave in ${data.nextWaveIn} seconds`, 'color: green; font-weight: bold');
        
        // Update UI with wave information
        if (this.uiManager) {
            // Ensure UI manager knows we're in the game screen
            this.uiManager.activeScreen = 'game';
            
            // Update wave info first
            this.uiManager.updateWaveInfo(data.waveNumber, 0);
            
            // Show wave end notification
            this._showWaveEndNotification(data.waveNumber);
            
            // Show countdown to next wave - ensure nextWaveIn is a number
            const nextWaveIn = typeof data.nextWaveIn === 'number' ? data.nextWaveIn : 10;
            console.log(`%cStarting countdown timer: ${nextWaveIn} seconds`, 'color: yellow');
            this.uiManager.showWaveCountdown(nextWaveIn);
            
            // Hide boss health bar if it was showing
            this.uiManager.updateBossInfo('', 0, 0, false);
        } else {
            console.warn('UIManager not available for wave end update');
        }
    }

    /**
     * Handle boss spawn event
     * @param {Object} data - Boss spawn data
     * @private
     */
    _onBossSpawn(data) {
        console.log(`Boss spawned for wave ${data.waveNumber} with ${data.health} HP`);
        
        // Show boss notification
        this._showBossSpawnNotification(data.waveNumber);
        
        // Update boss health bar
        if (this.uiManager) {
            const bossName = `Wave ${data.waveNumber} Boss`;
            this.uiManager.updateBossInfo(bossName, data.health, data.maxHealth, true);
        }
    }

    /**
     * Handle XP award event
     * @param {Object} data - XP award data
     * @private
     */
    _onXpAward(data) {
        console.log(`Received ${data.amount} XP from ${data.source || 'enemy'}`);
        
        // Show XP notification
        this._showXpNotification(data.amount);
        
        // Store XP with the player object
        if (this.player) {
            // If total XP is provided, use it; otherwise add to current
            if (data.total !== undefined) {
                this.player.xp = data.total;
            } else {
                this.player.xp = (this.player.xp || 0) + data.amount;
            }
            
            // Calculate level based on XP - simple calculation
            this.player.level = Math.floor(this.player.xp / 100) + 1;
            
            // Calculate XP needed for next level
            const nextLevelXP = 100;
            const currentLevelXP = this.player.xp % 100;
            
            console.log(`Updated player XP: ${this.player.xp}, Level: ${this.player.level}, Current level progress: ${currentLevelXP}/${nextLevelXP}`);
            
            // Update UI if manager exists - try all possible references
            if (this.uiManager) {
                console.log('Updating XP bar using this.uiManager');
                this.uiManager.updateXP(this.player.level, currentLevelXP, nextLevelXP);
            } else if (window.uiManager) {
                console.log('Updating XP bar using window.uiManager');
                window.uiManager.updateXP(this.player.level, currentLevelXP, nextLevelXP);
            } else {
                // Try to create a new UI manager as a last resort
                console.warn('No UI manager available, attempting to create one');
                this._ensureUIManager();
                
                if (this.uiManager) {
                    this.uiManager.updateXP(this.player.level, currentLevelXP, nextLevelXP);
                }
            }
        }
    }

    /**
     * Show wave start notification
     * @param {number} waveNumber - The wave number that started
     * @private
     */
    _showWaveStartNotification(waveNumber) {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'game-notification wave-start';
        notification.textContent = `Wave ${waveNumber} Started!`;
        
        // Add to document
        document.body.appendChild(notification);
            
        // Remove after animation
        setTimeout(() => {
            notification.classList.add('fade-out');
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 1000); // 1 second fade out
        }, 3000); // Show for 3 seconds
    }

    /**
     * Show wave end notification
     * @param {number} waveNumber - The wave number that ended
     * @private
     */
    _showWaveEndNotification(waveNumber) {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'game-notification wave-end';
        notification.textContent = `Wave ${waveNumber} Completed!`;
        
        // Add to document
        document.body.appendChild(notification);
        
        // Remove after animation
        setTimeout(() => {
            notification.classList.add('fade-out');
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 1000); // 1 second fade out
        }, 3000); // Show for 3 seconds
    }

    /**
     * Show boss spawn notification
     * @param {number} waveNumber - The current wave number
     * @private
     */
    _showBossSpawnNotification(waveNumber) {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'game-notification boss-spawn';
        notification.textContent = `Wave ${waveNumber} Boss has appeared!`;
        
        // Add to document
        document.body.appendChild(notification);
        
        // Add screen shake effect
        this._addScreenShake();
        
        // Remove after animation
        setTimeout(() => {
            notification.classList.add('fade-out');
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                }
            }, 1000); // 1 second fade out
        }, 3000); // Show for 3 seconds
    }

    /**
     * Show XP notification
     * @param {number} amount - The amount of XP gained
     * @private
     */
    _showXpNotification(amount) {
        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'game-notification xp-gain';
        notification.textContent = `+${amount} XP`;
        
        // Add to document
        document.body.appendChild(notification);
        
        // Random position near the center of the screen
        const offsetX = (Math.random() - 0.5) * 200;
        const offsetY = (Math.random() - 0.5) * 200;
        notification.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
        
        // Add floating animation
        notification.style.animation = 'xp-float 2s ease-out, fade-out 1s ease-in 1s forwards';
        
        // Remove after animation
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 2000); // Remove after 2 seconds
    }

    /**
     * Request server to start wave system if it hasn't started yet
     */
    requestWaveStart() {
        if (this.socket && this.socket.connected) {
            console.log('Requesting wave start from server');
            this.socket.emit('startWave');
        } else {
            console.warn('Cannot request wave start: not connected to server');
        }
    }

    /**
     * Toggle debug display of enemy health and positions
     */
    toggleEnemyDebug() {
        this.showEnemyDebug = !this.showEnemyDebug;
        console.log(`Enemy debug display ${this.showEnemyDebug ? 'enabled' : 'disabled'}`);
        
        // Create or update debug container
        if (this.showEnemyDebug) {
            if (!this.debugContainer) {
                this.debugContainer = document.createElement('div');
                this.debugContainer.style.position = 'absolute';
                this.debugContainer.style.top = '100px';
                this.debugContainer.style.left = '10px';
                this.debugContainer.style.backgroundColor = 'rgba(0,0,0,0.7)';
                this.debugContainer.style.color = 'white';
                this.debugContainer.style.padding = '10px';
                this.debugContainer.style.borderRadius = '5px';
                this.debugContainer.style.fontFamily = 'monospace';
                this.debugContainer.style.zIndex = '1000';
                document.body.appendChild(this.debugContainer);
            }
            
            // Force a server sync to get the latest enemy state
            if (this.socket) {
                this.socket.emit('syncEnemies');
                this.socket.emit('debugEnemies');
            }
            
            // Start updating the debug display
            this._updateEnemyDebugDisplay();
        } else if (this.debugContainer) {
            document.body.removeChild(this.debugContainer);
            this.debugContainer = null;
        }
    }

    /**
     * Update enemy debug display
     * @private
     */
    _updateEnemyDebugDisplay() {
        if (!this.showEnemyDebug || !this.debugContainer) return;
        
        if (this.enemyManager) {
            // Get all enemies
            const enemies = this.enemyManager.getEnemies();
            
            let html = '<h3>Enemy Debug</h3>';
            
            if (enemies.size === 0) {
                html += '<p>No enemies found</p>';
            } else {
                html += '<table>';
                html += '<tr><th>ID</th><th>Health</th><th>Position</th></tr>';
                
                enemies.forEach((enemy, id) => {
                    const position = enemy.position ? 
                        `(${enemy.position.x.toFixed(1)}, ${enemy.position.z.toFixed(1)})` : 
                        'Unknown';
                    
                    html += `<tr>
                        <td>${id.substring(0, 10)}...</td>
                        <td>${enemy.health}/${enemy.maxHealth}</td>
                        <td>${position}</td>
                    </tr>`;
                });
                
                html += '</table>';
                html += `<button onclick="window.game.testDamageEnemy()">Test Damage</button>`;
            }
            
            this.debugContainer.innerHTML = html;
        }
        
        // Update again in 500ms
        setTimeout(() => this._updateEnemyDebugDisplay(), 500);
    }

    /**
     * Test function to damage a random enemy
     */
    testDamageEnemy() {
        if (!this.enemyManager) return;
        
        const enemies = this.enemyManager.getEnemies();
        if (enemies.size === 0) {
            console.log('No enemies to damage');
            return;
        }
        
        // Get a random enemy
        const enemyIds = Array.from(enemies.keys());
        const randomIndex = Math.floor(Math.random() * enemyIds.length);
        const enemyId = enemyIds[randomIndex];
        
        console.log(`Sending test damage to enemy ${enemyId}`);
        
        // Send damage event directly to server
        if (this.socket) {
            this.socket.emit('enemyDamage', {
                enemyId: enemyId,
                damage: 50 // High damage to see clear effect
            });
            
            console.log(`Test damage event sent for enemy ${enemyId}`);
        }
    }

    /**
     * Initialize the UI components
     * @private
     */
    _initializeUI() {
        console.log('Initializing UI components...');
        
        // Set up wave start/end buttons
        const startWaveBtn = document.getElementById('start-wave-btn');
        if (startWaveBtn) {
            startWaveBtn.addEventListener('click', () => {
                console.log('Start wave button clicked');
                this.socket.emit('startWave');
            });
        } else {
            console.warn('Start wave button not found in DOM');
        }
        
        const syncEnemiesBtn = document.getElementById('sync-enemies-btn');
        if (syncEnemiesBtn) {
            syncEnemiesBtn.addEventListener('click', () => {
                console.log('Sync enemies button clicked');
                this.requestEnemySync();
            });
        } else {
            console.warn('Sync enemies button not found in DOM');
        }
        
        // Kill all enemies button
        const killAllBtn = document.getElementById('killAllBtn');
        if (killAllBtn) {
            killAllBtn.addEventListener('click', () => {
                console.log('Kill all button clicked');
                this.socket.emit('killAllEnemies', {
                    playerId: this.socket.id
                });
            });
        } else {
            console.warn('Kill all button not found in DOM');
        }
        
        // Make game object globally accessible for debugging
        window.game = this;
        
        console.log('UI components initialized');
    }

    /**
     * Handle playerUpdate event - called when a player's state changes
     * @param {Object} playerData - Updated player data
     * @private
     */
    _onPlayerUpdate(playerData) {
        try {
            // Log update for our own player
            if (this.socket && playerData.id === this.socket.id) {
                console.log(`Player update for local player: ${playerData.id}`);
                
                // Process health update if provided
                if (playerData.health !== undefined && this.player) {
                    // Get current health for comparison
                    const oldHealth = this.player.health;
                const newHealth = playerData.health;
                
                // Log significant health changes
                    if (Math.abs(oldHealth - newHealth) > 5) {
                        console.log(`Health change: ${oldHealth} -> ${newHealth}`);
                    }
                    
                    // Check if health is 0 or below and player isn't already dead
                    if (newHealth <= 0 && !this.player.isDead) {
                        console.log(`Player health is 0 or below, triggering death logic`);
                        
                        // Call the player death method
                        this.onPlayerDeath();
                        
                        // Mark player as dead
                        this.player.isDead = true;
                    }
                    
                    // Update the player health
                    this.player.health = newHealth;
                    
                    // Show damage effect if health decreased
                    if (oldHealth > newHealth) {
                        const damage = oldHealth - newHealth;
                        this._showDamageEffect(damage);
                    }
                }
                
                // Process isDead flag directly from server
                if (playerData.isDead !== undefined && this.player) {
                    console.log(`Server says player isDead: ${playerData.isDead}`);
                    
                    // If server says we're dead, make sure we're marked dead locally
                    if (playerData.isDead && !this.player.isDead) {
                        console.log(`Server marked player as dead, triggering death logic`);
                        this.onPlayerDeath();
                        this.player.isDead = true;
                    }
                }
                
                // Store player data globally for enemy targeting
                if (!window.players) {
                    window.players = new Map();
                }
                window.players.set(playerData.id, playerData);
            } 
            // Update for a remote player
            else if (this.remotePlayersManager) {
                this.remotePlayersManager.updatePlayerData(playerData);
            }
        } catch (e) {
            console.error("Error in _onPlayerUpdate:", e);
        }
    }

    /**
     * Handle when a player dies
     * @param {Player} player - The player that died (optional - uses this.player if not provided)
     */
    onPlayerDeath(player) {
        // Use this.player if player parameter is not provided
        const deadPlayer = player || this.player;
        
        if (!deadPlayer) {
            console.error('Cannot handle player death: No player object available');
            return;
        }
        
        console.log(`Player has died: ${deadPlayer.name || 'unknown'}`);
        
        // Set the player as dead
        deadPlayer.isDead = true;
        
        // Force health to 0
        deadPlayer.health = 0;
        
        // Check if this is the local player
        if (deadPlayer === this.player) {
            console.log('Local player died');
            
            // Disable controls
            if (this.movementController) {
                this.movementController.enabled = false;
                console.log('Movement controller disabled due to death');
            }
            
            // Hide player model if available
            if (deadPlayer.modelMesh) {
                deadPlayer.modelMesh.visible = false;
                console.log('Player model hidden due to death');
            }
            
            // If we have a camera, store its original position for later
            if (this.camera) {
                this._originalCameraPosition = {
                    position: this.camera.position.clone(),
                    target: this.player.position.clone()
                };
                
                // Optionally move the camera for death view (slightly above player)
                const deathCamPos = this.player.position.clone().add(new THREE.Vector3(0, 10, 5));
                this.camera.position.copy(deathCamPos);
                this.camera.lookAt(this.player.position);
            }
            
            // Show death message
            this._showDeathMessage();            
            
            // Emit death event to server
            if (this.socket) {
                this.socket.emit('playerDied', { id: this.socket.id });
                console.log('Sent playerDied event to server');
            }
        }
    }
    
    /**
     * Show death message with respawn options
     * @private
     */
    _showDeathMessage() {
        // Create container for death message
        const container = document.createElement('div');
        container.className = 'death-overlay';
        container.style.position = 'fixed';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.backgroundColor = 'rgba(0,0,0,0.7)';
        container.style.color = 'white';
        container.style.flexDirection = 'column';
        container.style.justifyContent = 'center';
        container.style.alignItems = 'center';
        container.style.zIndex = '1000';
        if (this.renderLoop.isActive()){
            container.style.display = 'flex';
        } else{
            container.style.display = 'none';
        }        
        
        // Create death message
        const message = document.createElement('h1');
        message.textContent = 'You Died!';
        message.style.fontSize = '48px';
        message.style.marginBottom = '20px';
        message.style.color = '#ff3030';
        container.appendChild(message);
        
        // Create respawn countdown
        const countdown = document.createElement('p');
        countdown.innerHTML = 'Respawning in <span id="respawn-countdown">5</span> seconds...';
        countdown.style.fontSize = '24px';
        countdown.style.marginBottom = '40px';
        container.appendChild(countdown);
        
        // Add to document
        document.body.appendChild(container);
        
        // Start countdown
        let timeLeft = 5;
        const countdownElement = document.getElementById('respawn-countdown');
        const countdownInterval = setInterval(() => {
            timeLeft--;
            if (countdownElement) {
                countdownElement.textContent = timeLeft;
            }
            
            if (timeLeft <= 0) {
                clearInterval(countdownInterval);
                this.respawnPlayer();
                
                // Remove death message
                if (container.parentNode) {
                    container.parentNode.removeChild(container);
                }
            }
        }, 1000);
    }
    
    /**
     * Respawn the player
     */
    respawnPlayer() {
        console.log('Respawning player...');
        
        if (!this.player) {
            console.error('Cannot respawn: No player object available');
            return;
        }
        
        // Call player's respawn method if available
        if (typeof this.player.respawn === 'function') {
            this.player.respawn();
        } else {
            // Fallback respawn implementation
            this.player.health = this.player.maxHealth || 100;
            this.player.isDead = false;
            
            // Show player model if available
            if (this.player.modelMesh) {
                this.player.modelMesh.visible = true;
            }
        }
        
        // Reset camera if we stored original position
        if (this.camera && this._originalCameraPosition) {
            this.camera.position.copy(this._originalCameraPosition.position);
            this.camera.lookAt(this._originalCameraPosition.target);
        }
        
        // Re-enable controls
        if (this.movementController) {
            this.movementController.enabled = true;
        }
        
        // Send respawn event to server
        if (this.socket) {
            this.socket.emit('playerRespawn', { id: this.socket.id });
            console.log('Sent playerRespawn event to server');
        }
        
        // Update UI
        if (this.uiManager) {
            this.uiManager.updateHealth(this.player.health, this.player.maxHealth || 100, false);
        }
        
        console.log('Player respawned successfully');
    }

    /**
     * Ensure there is a valid UI manager
     * @private
     */
    _ensureUIManager() {
        // First check global reference
        if (window.uiManager && !this.uiManager) {
            console.log('Using existing global UIManager');
            this.uiManager = window.uiManager;
            return this.uiManager;
        }
        
        // Then check local reference and update global
        if (this.uiManager && !window.uiManager) {
            console.log('Setting global UIManager from local reference');
            window.uiManager = this.uiManager;
            return this.uiManager;
        }
        
        // If no UIManager is found, log a warning
        if (!this.uiManager) {
            console.warn('No UIManager instance found');
        }
        
        return this.uiManager;
    }

    /**
     * Register socket event handlers
     * @private
     */
    _registerSocketEventHandlers() {
        if (!this.socket) {
            console.error('Cannot register socket handlers: no socket available');
            return;
        }

        console.log('Registering socket event handlers...');

        try {
            // Register for connection events
            this.socket.on('connect', this._onConnect.bind(this));
            this.socket.on('disconnect', this._onDisconnect.bind(this));
            
            // Register for login response
            this.socket.on('login-success', this._onLoginSuccess.bind(this));
            this.socket.on('login-failed', this._onLoginFailed.bind(this));
            
            // Register for player update events
            this.socket.on('playerUpdate', this._onPlayerUpdate.bind(this));
            this.socket.on('playerDied', this._onPlayerDied.bind(this));
            this.socket.on('playerHealthCritical', this._onPlayerHealthCritical.bind(this));
            this.socket.on('playerInvincibilityChange', this._onPlayerInvincibilityChange.bind(this));
            this.socket.on('playerInvincibleHit', this._onPlayerInvincibleHit.bind(this));
            
            // Register for new player events
            this.socket.on('newPlayer', this._onNewPlayer.bind(this));
            this.socket.on('playerDisconnected', this._onPlayerDisconnected.bind(this));
            
            // Register for self position events from server
            this.socket.on('selfPosition', this._onSelfPosition.bind(this));
            
            // Register for XP events
            this.socket.on('xpAward', this._onXpAward.bind(this));
            
            // Register for wave events
            this.socket.on('waveStart', this._onWaveStart.bind(this));
            this.socket.on('waveUpdate', this._onWaveUpdate.bind(this));
            this.socket.on('waveEnd', this._onWaveEnd.bind(this));
            
            // Register for game notifications
            this.socket.on('gameNotification', this._onGameNotification.bind(this));
            
            console.log('Socket event handlers registered');
        } catch (error) {
            console.error('Error registering socket handlers:', error);
        }
    }

    /**
     * Test function to kill the player (set health to 0)
     */
    testPlayerDeath() {
        if (!this.player) {
            console.warn('Cannot test player death: no player object');
            return;
        }
        
        console.log('Testing player death - setting health to 0');
        
        // Set player health to 0
        this.player.health = 0;
        
        // Update the player's visual state (hide model)
        const isDead = this.player.takeDamage(0);
        
        // Update UI health bar
        if (this.uiManager) {
            this.uiManager.updateHealth(0, this.player.maxHealth, true);
        } else if (window.uiManager) {
            window.uiManager.updateHealth(0, this.player.maxHealth, true);
        } else {
            this._updateHealthBarDirect(0, this.player.maxHealth, true);
        }
        
        // Show death message UI
        this._showDeathMessage();
        
        // Disable player movement
        if (this.movementController) {
            this.movementController.enabled = false;
        }
        
        console.log('Player death test complete');
    }
    
    /**
     * Handle when a player respawns
     * @param {Player} player - The player that respawned
     */
    onPlayerRespawn(player) {
        if (player === this.player) {
            // Re-enable controls
            this.controlsEnabled = true;
            
            // Reset camera if we stored original position
            if (this.camera && this._originalCameraPosition) {
                this.camera.position.copy(this._originalCameraPosition.position);
                this.camera.lookAt(this._originalCameraPosition.target);
            }
        }
    }

    /**
     * Handle player invincibility change event from server
     * @param {Object} data - Invincibility change data
     * @private
     */
    _onPlayerInvincibilityChange(data) {
        console.log('Received playerInvincibilityChange event:', data);
        
        try {
            // Check if this is for the local player
            if (data.id === this.socket.id && this.player) {
                // Set invincibility on local player
                this.player.setInvincible(data.isInvincible, data.duration);
                
                // Log update
                console.log(`Local player invincibility set to ${data.isInvincible} for ${data.duration}ms`);
                
                // Show visual effect if invincible
                if (data.isInvincible) {
                    this._showInvincibilityEffect();
                }
            } else if (this.remotePlayers && this.remotePlayers.players[data.id]) {
                // Handle remote player invincibility state
                const remotePlayer = this.remotePlayers.players[data.id];
                
                // Set invincibility property if exists
                if (remotePlayer.setInvincible) {
                    remotePlayer.setInvincible(data.isInvincible, data.duration);
                } else {
                    // Fallback to just setting the property
                    remotePlayer.isInvincible = data.isInvincible;
                }
                
                console.log(`Remote player ${data.id} invincibility set to ${data.isInvincible}`);
            }
        } catch (error) {
            console.error('Error handling playerInvincibilityChange event:', error);
        }
    }
    
    /**
     * Handle player invincible hit event from server
     * @param {Object} data - Invincible hit data
     * @private
     */
    _onPlayerInvincibleHit(data) {
        console.log('Received playerInvincibleHit event:', data);
        
        try {
            // Check if this is for the local player
            if (data.id === this.socket.id) {
                // Show invincibility hit effect
                this._showInvincibilityHitEffect(data.damageBlocked);
                
                // Log blocked damage
                console.log(`Local player blocked ${data.damageBlocked} damage due to invincibility`);
            }
        } catch (error) {
            console.error('Error handling playerInvincibleHit event:', error);
        }
    }
    
    /**
     * Show visual effect for invincibility
     * @private
     */
    _showInvincibilityEffect() {
        console.log('Showing invincibility effect');
        
        try {
            // Create a blue shield effect particle system or overlay
            const container = document.getElementById('gameContainer') || document.body;
            
            // Create invincibility overlay if it doesn't exist
            let overlay = document.getElementById('invincibilityOverlay');
            if (!overlay) {
                overlay = document.createElement('div');
                overlay.id = 'invincibilityOverlay';
                overlay.style.position = 'fixed';
                overlay.style.top = '0';
                overlay.style.left = '0';
                overlay.style.width = '100%';
                overlay.style.height = '100%';
                overlay.style.pointerEvents = 'none';
                overlay.style.zIndex = '10';
                overlay.style.boxShadow = 'inset 0 0 50px rgba(0, 100, 255, 0.5)';
                overlay.style.border = '2px solid rgba(0, 150, 255, 0.7)';
                overlay.style.animation = 'invincibility-pulse 1.5s infinite';
                container.appendChild(overlay);
                
                // Add keyframe animation if it doesn't exist
                if (!document.getElementById('invincibilityKeyframes')) {
                    const style = document.createElement('style');
                    style.id = 'invincibilityKeyframes';
                    style.textContent = `
                        @keyframes invincibility-pulse {
                            0% { opacity: 0.1; }
                            50% { opacity: 0.3; }
                            100% { opacity: 0.1; }
                        }
                    `;
                    document.head.appendChild(style);
                }
            } else {
                // Reset animation
                overlay.style.display = 'block';
                overlay.style.animation = 'none';
                setTimeout(() => {
                    overlay.style.animation = 'invincibility-pulse 1.5s infinite';
                }, 10);
            }
            
            // Hide overlay after duration
            setTimeout(() => {
                if (overlay) {
                    overlay.style.display = 'none';
                }
            }, 5000);
        } catch (error) {
            console.error('Error showing invincibility effect:', error);
        }
    }
    
    /**
     * Show visual effect for blocked damage during invincibility
     * @param {number} damageBlocked - Amount of damage blocked
     * @private
     */
    _showInvincibilityHitEffect(damageBlocked) {
        console.log('Showing invincibility hit effect');
        
        try {
            // Flash the invincibility overlay to indicate a hit was blocked
            const overlay = document.getElementById('invincibilityOverlay');
            if (overlay) {
                // Temporarily change the color to indicate blocked hit
                overlay.style.boxShadow = 'inset 0 0 50px rgba(0, 200, 255, 0.8)';
                overlay.style.border = '3px solid rgba(0, 220, 255, 0.9)';
                
                // Create a floating text element showing the blocked damage
                const blockedText = document.createElement('div');
                blockedText.textContent = `BLOCKED: ${damageBlocked}`;
                blockedText.style.position = 'absolute';
                blockedText.style.color = '#00DDFF';
                blockedText.style.fontWeight = 'bold';
                blockedText.style.fontSize = '20px';
                blockedText.style.textShadow = '0 0 5px #0066FF';
                
                // Position near the center of the screen
                blockedText.style.left = `${50 + (Math.random() * 20 - 10)}%`;
                blockedText.style.top = `${50 + (Math.random() * 20 - 10)}%`;
                blockedText.style.zIndex = '12';
                blockedText.style.pointerEvents = 'none';
                
                // Add animation
                blockedText.style.animation = 'blocked-text-float 1.5s forwards';
                
                // Add to document
                document.body.appendChild(blockedText);
                
                // Add keyframe animation if it doesn't exist
                if (!document.getElementById('blockedTextKeyframes')) {
                    const style = document.createElement('style');
                    style.id = 'blockedTextKeyframes';
                    style.textContent = `
                        @keyframes blocked-text-float {
                            0% { transform: scale(0.7); opacity: 0; }
                            20% { transform: scale(1.2); opacity: 1; }
                            80% { transform: scale(1.0); opacity: 0.9; }
                            100% { transform: scale(0.8) translateY(-30px); opacity: 0; }
                        }
                    `;
                    document.head.appendChild(style);
                }
                
                // Remove the text after animation completes
                setTimeout(() => {
                    if (blockedText.parentNode) {
                        blockedText.parentNode.removeChild(blockedText);
                    }
                }, 1500);
                
                // Reset overlay after brief flash
                setTimeout(() => {
                    overlay.style.boxShadow = 'inset 0 0 50px rgba(0, 100, 255, 0.5)';
                    overlay.style.border = '2px solid rgba(0, 150, 255, 0.7)';
                }, 300);
            }
        } catch (error) {
            console.error('Error showing invincibility hit effect:', error);
        }
    }

    /**
     * Handle player died event from server
     * @param {Object} data - Player died event data
     * @private
     */
    _onPlayerDied(data) {
        console.log('Received explicit playerDied event:', data);
        
        try {
            // Handle for local player
            if (data.id === this.socket.id && this.player) {
                console.log('Local player death confirmed by server');
                
                // Set player health to 0
                this.player.health = 0;
                this.player.isDead = true;
                
                // Update UI health display
                if (this.uiManager) {
                    this.uiManager.updateHealth(0, this.player.maxHealth, true);
                }
                
                // Hide player model
                if (this.player.modelMesh) {
                    this.player.modelMesh.visible = false;
                }
                
                // Trigger death sequence if not already triggered
                if (!this._deathSequenceTriggered) {
                    this._deathSequenceTriggered = true;
                    this.onPlayerDeath(this.player);
                }
            }
            // Handle for remote players
            else if (this.remotePlayers && this.remotePlayers.players[data.id]) {
                console.log(`Remote player ${data.id} death confirmed by server`);
                
                const remotePlayer = this.remotePlayers.players[data.id];
                remotePlayer.health = 0;
                remotePlayer.isDead = true;
                
                // Hide remote player model
                if (remotePlayer.modelMesh) {
                    remotePlayer.modelMesh.visible = false;
                }
            }
        } catch (error) {
            console.error('Error handling playerDied event:', error);
        }
    }
    
    /**
     * Handle player health critical event from server
     * @param {Object} data - Player health critical event data
     * @private
     */
    _onPlayerHealthCritical(data) {
        console.log('Received playerHealthCritical event:', data);
        
        try {
            // Only handle for local player
            if (data.id === this.socket.id && this.player) {
                // Update player health
                this.player.health = data.health;
                this.player.isDead = data.isDead;
                
                // Update UI health display
                if (this.uiManager) {
                    this.uiManager.updateHealth(data.health, data.maxHealth, data.isDead);
                }
                
                // Show critical health effect if health is low but player is not dead
                if (data.health <= 25 && !data.isDead) {
                    this._showCriticalHealthEffect();
                }
                
                // Handle player death if server indicates player is dead
                if (data.isDead && !this._deathSequenceTriggered) {
                    console.log('Server indicates player is dead via critical health event');
                    this._deathSequenceTriggered = true;
                    
                    // Hide player model
                    if (this.player.modelMesh) {
                        this.player.modelMesh.visible = false;
                    }
                    
                    // Trigger death sequence
                    this.onPlayerDeath(this.player);
                }
            }
        } catch (error) {
            console.error('Error handling playerHealthCritical event:', error);
        }
    }
} 